import {useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'

import {Env} from '../../env'
import {RIF, useTranslation} from '../../lib'
import {
  SessionEndReason,
  SessionEndReasonState,
  selectProfile,
  selectAuth,
  selectSessionState,
  selectTheme,
} from '../../store'

import {LoginForm} from '..'

/**
 * How long after a forced sign-out this page still explains it. Past the window the
 * message is gone -- recent account activity has no business lingering on a shared machine, and
 * a reason this old is no longer a fair account of why the visitor is looking at a login form.
 */
export const SIGN_OUT_REASON_VISIBLE_MS = 600000

const END_REASON_MESSAGE_KEY: Readonly<Record<SessionEndReason, string>> = {
  [SessionEndReason.SecurityChangeEmail]: 'sign_in.signed_out_email_change_message',
  [SessionEndReason.SecurityChangeMfa]: 'sign_in.signed_out_mfa_change_message',
  [SessionEndReason.SessionEnded]: 'sign_in.session_ended_message',
}

/**
 * The reason the login page may state, or null for "say nothing".
 *
 * The slice is hydrated straight out of `sessionStorage` and nothing validates it on the way back
 * in, so a reason this build does not know, a missing or non-numeric stamp, and a stamp ahead of
 * the clock (the machine's time moved backwards) all have to render nothing. A future stamp is
 * refused rather than shown: it would otherwise outlive its window for as long as the skew lasts.
 */
export const endReasonMessageKey = (endReason: SessionEndReasonState | null | undefined, now: number) => {
  if (!endReason) return null

  const messageKey = END_REASON_MESSAGE_KEY[endReason.reason]
  if (!messageKey) return null

  const at = endReason.at
  if (typeof at !== 'number' || !Number.isFinite(at)) return null

  const age = now - at
  if (age < 0 || age >= SIGN_OUT_REASON_VISIBLE_MS) return null

  return messageKey
}

export const LoginPage = () => {
  const {t} = useTranslation()
  const {color, pad, fontSize, fontWeight, borderRadius} = selectTheme()
  const navigate = useNavigate()
  const accessToken = selectAuth()?.accessToken
  const isRefreshing = selectAuth()?.isRefreshing
  const profile = selectProfile()
  const {endReason} = selectSessionState()
  useEffect(() => {
    if (isRefreshing) return
    if (accessToken && profile?.id) navigate('/project')
  }, [accessToken, isRefreshing, profile?.id])

  // The window is a live rule, not a check made once on arrival: a page left open past it drops
  // the message on its own. The clock is re-read whenever the reason changes, so a sign-out that
  // happens while this page is already mounted is measured from its own stamp.
  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    setNowMs(Date.now())

    const at = endReason?.at
    if (typeof at !== 'number' || !Number.isFinite(at)) return

    const remainingMs = at + SIGN_OUT_REASON_VISIBLE_MS - Date.now()
    if (remainingMs <= 0) return

    const timer = window.setTimeout(() => setNowMs(Date.now()), remainingMs)
    return () => window.clearTimeout(timer)
  }, [endReason])

  const messageKey = endReasonMessageKey(endReason, nowMs)

  if (Env.shared.webPackModeIsDevelopment()) {
    for (const [key, value] of Object.entries(Env.shared.dump())) {
      console.log(`${key}: ${value}`)
    }
  }

  return (
    <div
      css={{
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 102,
        width: '100vw',
        height: '100vh',
        // the notice adds a row above the form, so a short viewport scrolls instead of clipping it
        overflowY: 'auto',
        background: color.background,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: '100px',
      }}>
      <h1
        css={{
          marginTop: '24px',
          marginBottom: '16px',
          fontSize: fontSize.h3,
          fontWeight: fontWeight.thick,
          color: color.primary,
        }}>
        Sign in to Labfront
      </h1>

      {/* Why this tab was signed out, in the one place the user is looking for it. It is
          limited to what this tab already knew -- the refresh 401 carries no reason of its own. */}
      {RIF(
        Boolean(messageKey),
        <div
          data-testid='login_page_end_reason_message'
          role='status'
          aria-live='polite'
          css={{
            boxSizing: 'border-box',
            width: '446px',
            maxWidth: 'calc(100vw - 32px)',
            padding: `${pad.small} ${pad.medium}`,
            borderRadius: borderRadius.default,
            border: `1px solid ${color.border.warning}`,
            background: color.surface.attention,
            color: color.textIcon.title,
            fontSize: fontSize.h6,
            textAlign: 'center',
          }}>
          {t(messageKey as string)}
        </div>,
      )}

      <LoginForm />
    </div>
  )
}
